import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { SubscriptionSchema, SubscriptionCreateInputSchema, SubscriptionUpdateInputSchema, type Subscription, type SubscriptionCreateInput, type SubscriptionUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `subscription` (engagement récurrent, org-wide).
 *
 * Calque le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`SubscriptionCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 */

export type { SubscriptionCreateInput };

/** Nom de la collection Firestore de la famille subscription. */
export const SUBSCRIPTIONS_COLLECTION = 'subscriptions';

/**
 * Repository typé pour la famille `subscription`.
 * Toute lecture est validée par `SubscriptionSchema`, toute écriture par
 * `SubscriptionCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface SubscriptionRepository {
  create(input: SubscriptionCreateInput): Promise<Subscription>;
  update(id: string, patch: SubscriptionUpdateInput): Promise<Subscription>;
  list(options?: ListOptions): Promise<Subscription[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Subscription | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Subscription>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Subscription>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Subscription>;
}

const subscriptionConverter = createConverter<Subscription>((raw: DocumentData) => SubscriptionSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createSubscriptionRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): SubscriptionRepository {
  const col = collection(db, SUBSCRIPTIONS_COLLECTION).withConverter(subscriptionConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Subscription>(db, col, SUBSCRIPTIONS_COLLECTION, getActorId);

  return {
    async create(input: SubscriptionCreateInput): Promise<Subscription> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = SubscriptionCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.productId !== undefined) optional['productId'] = fields.productId;
      if (fields.planRef !== undefined) optional['planRef'] = fields.planRef;
      if (fields.billingPeriod !== undefined) optional['billingPeriod'] = fields.billingPeriod;
      if (fields.renewalMode !== undefined) optional['renewalMode'] = fields.renewalMode;
      if (fields.status !== undefined) optional['status'] = fields.status;
      if (fields.startAt !== undefined) optional['startAt'] = fields.startAt;
      if (fields.endAt !== undefined) optional['endAt'] = fields.endAt;
      if (fields.nextInvoiceAt !== undefined) optional['nextInvoiceAt'] = fields.nextInvoiceAt;
      if (fields.agreementId !== undefined) optional['agreementId'] = fields.agreementId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        contactId: fields.contactId,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Subscription);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return SubscriptionSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: SubscriptionUpdateInput): Promise<Subscription> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = SubscriptionUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, SUBSCRIPTIONS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`subscription ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Subscription[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Subscription | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
